<template>
  <div class="lab">
    <div class="lab__attempted"></div>
    <div class="lab__content">
      <div class="lab__progress">
        <div class="lab__flag-progress">
          <CpeLabProgress :currentValue="flagCount" :maxValue="totalFlags" />
        </div>
        <div class="skills-card">
          <h2 class="skills-card__title">Test your skills</h2>
          <p
            v-if="skillCheckLab.user_status_modified"
            class="skills-card__last-tested"
          >
            Last tested: {{ lastTestedDays }}
          </p>
          <div v-if="!isLabFinished" class="skills-card__credits">
            <span>CPE credits available: {{ skillCheckLab.cpe_credits }}</span>
            <InfoIcon
              v-tooltip="{
                content: isCpeProgramEnabled
                  ? 'Score 100% to earn credits'
                  : tooltipText,
                placement: 'right',
              }"
              size="14"
              color="var(--white)"
            />
          </div>
          <p
            v-if="isLabFinished && isCpeProgramEnabled"
            class="skills-card__credits"
            :style="{ color: 'var(--positive-light)' }"
          >
            CPE credits earned: {{ skillCheckLab.cpe_credits }}
          </p>
        </div>
      </div>
      <div class="lab__description">
        <div class="lab__description-content">
          <div class="lab__description-heading">{{ labName }}</div>
          <div class="lab__description-text">Overview: {{ labOverview }}</div>
          <div class="lab__description-tags">
            <div
              v-for="tag in visibleTags"
              :key="tag"
              class="lab__description-tag-wrapper"
            >
              <PillBadge :text="tag" translucid sober />
            </div>
          </div>
        </div>
        <div class="lab__button">
          <button class="btn btn--primary" @click="openLab">
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PillBadge from '@/components/shared/PillBadge'
import CpeLabProgress from './CpeLabProgress.vue'
import CONSTANTS from '@/constants'
import { hasContentAccess } from '@/helpers'
import { mapState, mapGetters } from 'vuex'
import { InfoIcon } from 'vue-feather-icons'
export default {
  name: 'CheckpointLab',
  components: {
    PillBadge,
    CpeLabProgress,
    InfoIcon,
  },
  props: {
    skillCheckLab: {
      type: Object,
      required: true,
    },
    parentId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      isCpeProgramEnabled: 'auth/isCpeProgramEnabled',
    }),
    ...mapState({
      course: state => state.courses.currentCourse,
    }),
    isLabFinished() {
      return (
        this.skillCheckLab.user_status ===
        CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
    },
    tooltipText() {
      return CONSTANTS.CPE.CPE_PROGRAM_CONTENT_TOOLTIP_MSG
    },
    labName() {
      return this.skillCheckLab.name
    },
    lastTestedDays() {
      const lastTested = new Date(this.skillCheckLab.user_status_modified)
      const today = new Date()
      lastTested.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      const diffTime = today - lastTested
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) {
        return 'Today'
      } else if (diffDays === 1) {
        return 'Yesterday'
      } else {
        return `${diffDays} days ago`
      }
    },
    cpeCredits() {
      if (this.isLabFinished) {
        return `${this.skillCheckLab.cpe_credits}/${this.skillCheckLab.cpe_credits}`
      } else {
        return `0/${this.skillCheckLab.cpe_credits}`
      }
    },
    flagCount() {
      if (this.isLabFinished) {
        return this.skillCheckLab?.user_flags?.length || 0
      } else {
        return (
          this.skillCheckLab?.user_flags?.filter(flag => flag.has_passed)
            ?.length || 0
        )
      }
    },
    totalFlags() {
      return this.skillCheckLab.cpe_credits / 2
    },
    labOverview() {
      return this.skillCheckLab.description || ''
    },
    visibleTags() {
      return [...this.skillCheckLab.tags]
    },
    buttonText() {
      return this.hasSkillCheckAccess ? 'Open' : 'Get Access'
    },
    hasSkillCheckAccess() {
      const content = {
        type: CONSTANTS.CONTENT_TYPES.LAB,
      }
      const file = this.skillCheckLab
      const { user_status: status, lab_type: labType } = file
      content.file = file
      content.status = status
      content.labType = labType
      const hasAccess = hasContentAccess({
        parent: this.course,
        content,
      })
      return hasAccess
    },
    checkoutLink() {
      return `https://${process.env.VUE_APP_CHECKOUT_URL}?product_selected=ine_premium`
    },
  },
  methods: {
    async openLab() {
      if (!this.hasSkillCheckAccess) {
        window.location.href = this.checkoutLink
        return
      }
      this.$emit('toggle-content', CONSTANTS.CONTENT_TYPES.LAB)
      const route = `${this.$route.name}-lab`
      const params = { labId: this.skillCheckLab.id }
      params.id = this.parentId
      this.$router.push({
        name: route,
        params,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.lab {
  background: var(--glass-mid-gray);
  border-radius: 10px;
  font-family: 'CircularStd-Book';
  height: auto;
  min-height: 285px;
  display: flex;
  overflow: hidden;
  margin-bottom: 40px;

  &__attempted {
    flex: 0 0 10px;
    background-color: var(--positive-light);
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &__content {
    flex: 1;
    display: flex;
  }

  &__progress {
    flex: 0 0 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
  }

  &__description {
    flex: 1;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-top: 20px;
    padding-bottom: 20px;
    padding-right: 30px;
    padding-left: 30px;
  }

  &__description-content {
    flex: 1;
    padding-right: 20px;
  }

  &__description-heading {
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    color: var(--white);
    margin-bottom: 10px;
  }

  &__description-text {
    color: var(--light-blue-gray);
    font-size: 16px;
    font-weight: 450;
    line-height: 24px;
    margin-bottom: 15px;
  }

  &__description-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
  }
}

.skills-card {
  $this: &;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0;
  gap: 4px;

  &__title {
    width: 240px;
    height: 24px;
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    color: var(--white);
    margin: 0;
    align-self: stretch;
  }

  &__last-tested,
  &__credits {
    width: 240px;
    height: 14px;
    font-style: normal;
    font-weight: 450;
    font-size: 12px;
    line-height: 14px;
    text-align: center;
    color: var(--light-blue-gray);
    margin: 0;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
}

@media (max-width: 1024px) {
  .lab {
    &__content {
      flex-direction: column;
    }

    &__progress,
    &__description {
      flex: 0 0 auto;
      width: 100%;
      flex-direction: column;
    }
  }
}
</style>
